import { useSyncExternalStore } from "react";

import {
  getStoredKeys,
  getStoredValue,
  setStoredValue,
} from "src/hooks/use-persisted-state";

export const SUPPORTED_AGENTS_MODELS = [
  "gpt-oss-120b",
  "claude-sonnet-4-5",
  "claude-opus-4-5-20251101",
  "claude-haiku-4-5-20251001",
  "opus",
  "sonnet",
  "minimax-m2p1",
] as const;

type Settings = {
  isDevMode: boolean;
  // Allow any string so users can enter custom model names not in the supported list
  model?: string;
  showAllSessions?: boolean;
  /**
   * When on, new Ad Studio chats this user creates start hidden. Opt-in and
   * off by default — with it off, creation payloads are unchanged. Only takes
   * effect alongside `isDevMode`, which is what surfaces the sidebar's
   * "Hidden chats" section and the composer's "Hidden" chip; without it a
   * hidden chat would have nowhere to surface.
   */
  hideNewAdStudioChats?: boolean;
};

/**
 * Dev settings are per person, not per workspace: staff hop between customer
 * workspaces all day and re-toggling dev mode in each one was busywork. So the
 * whole object lives under one unprefixed key rather than
 * `${workspaceId}.agents.settings`.
 *
 * Nothing in here is a workspace-scoped id. `model` is an inference model name
 * ("claude-sonnet-4-5", "opus") that travels fine; the workspace-scoped parent
 * model id is a separate concern under its own per-workspace key (see
 * `use-parent-model.ts`) and is deliberately not part of this object.
 */
const SETTINGS_KEY = "agents.settings";

const LEGACY_KEY_SUFFIX = ".agents.settings";

const DEFAULT_SETTINGS: Settings = {
  isDevMode: false,
  model: undefined,
};

/**
 * Moves pre-migration per-workspace settings to the global key, so switching
 * this store from per-workspace to global never silently resets someone's dev
 * mode.
 *
 * Runs once at module load, before any hook instance exists, and that timing
 * is the point rather than an optimization: `setSettings` merges onto whatever
 * the global key currently holds. A migration that ran during render instead
 * would depend on the user context having resolved and, if a write landed
 * before it, bury the legacy values under a defaults-based object permanently.
 *
 * Not needing a workspace id also rescues staff whose settings live under a
 * workspace they are not currently in, which is correct now that the setting
 * is per person rather than per workspace.
 *
 * The legacy keys are left in place: they cost nothing and keep a revert cheap.
 *
 * Exported for tests, which need to seed storage before it runs.
 */
export const adoptLegacySettings = (): void => {
  const global = getStoredValue<Settings | undefined>(SETTINGS_KEY, undefined);
  if (global !== undefined) {
    return;
  }

  const legacyKeys = getStoredKeys()
    .filter(
      (key) =>
        key.endsWith(LEGACY_KEY_SUFFIX) &&
        // `undefined.agents.settings` is junk the pre-migration hook wrote on
        // renders where the user context had not resolved yet.
        !key.startsWith("undefined."),
    )
    // A user with several workspaces has several legacy entries and all of
    // them are equally valid now. Sorting makes the pick independent of
    // storage order; dev mode being on then wins, since that is the setting
    // whose loss a staff user would actually notice.
    .sort();

  let adopted: Settings | undefined;
  for (const key of legacyKeys) {
    const legacy = getStoredValue<Settings | undefined>(key, undefined);
    if (legacy === undefined) {
      continue;
    }
    if (legacy.isDevMode) {
      adopted = legacy;
      break;
    }
    adopted ??= legacy;
  }

  if (adopted !== undefined) {
    setStoredValue(SETTINGS_KEY, adopted);
  }
};

adoptLegacySettings();

const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  // Another tab toggling dev mode toggles it here too.
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
};

let snapshotJson: string | undefined;
let snapshot: Settings = DEFAULT_SETTINGS;

// Storage reads parse a fresh object every time, so hand React the last one until the stored value changes.
const getSnapshot = (): Settings => {
  const stored = getStoredValue<Settings>(SETTINGS_KEY, DEFAULT_SETTINGS);
  const json = JSON.stringify(stored);
  if (json !== snapshotJson) {
    snapshotJson = json;
    snapshot = {
      isDevMode: stored.isDevMode ?? false,
      model: stored.model,
      showAllSessions: stored.showAllSessions ?? false,
      hideNewAdStudioChats: stored.hideNewAdStudioChats ?? false,
    };
  }
  return snapshot;
};

const setSettings = (updates: Partial<Settings>) => {
  // Merge onto what is actually stored, so a write from another tab is not clobbered.
  setStoredValue(SETTINGS_KEY, {
    ...getStoredValue<Settings>(SETTINGS_KEY, DEFAULT_SETTINGS),
    ...updates,
  });
  for (const listener of listeners) listener();
};

/**
 * One store shared by every consumer, so toggling dev mode anywhere (the nav's
 * user menu, the Dev settings dialog) updates every mounted consumer at once.
 */
export const useDevSettings = (): {
  settings: Settings;
  setSettings: (settings: Partial<Settings>) => void;
} => ({ settings: useSyncExternalStore(subscribe, getSnapshot), setSettings });
